import { InternalRefetchQueryDescriptor } from "@apollo/client";
import { assertNever } from "@msys/common";
import { getItemPlaceholderKind, ItemPlaceholderKind } from "../helpers.js";
import type { PlaceholderPick } from "./placeholderPick.js";
import { PlaceholderSelection_ItemFragment } from "./PlaceholderSelection.generated.js";
import { ProductPlaceholderSelectionPanel } from "./ProductPlaceholderSelectionPanel.js";
import {
  PlaceholderItemCreation,
  TemplatePlaceholderSelectionPanel,
} from "./TemplatePlaceholderSelectionPanel.js";

interface Props {
  projectId: string | null;
  docId: string;
  item: PlaceholderSelection_ItemFragment;
  /** Overrides the kind derived from the item, for an item that is both. */
  kind?: ItemPlaceholderKind;
  expandedItemIds: string[] | undefined;
  onUpdateDataRefetchQueries?: InternalRefetchQueryDescriptor[];
  refetchWizardQuestions: () => Promise<unknown>;
  handleClose: () => void;
  onApplied?: (pick: PlaceholderPick) => Promise<void>;
  /** Product case only: lays the product search out for a narrow host such as the chat. */
  embedded?: boolean;
  /** Template case only: offers the fill action for an empty placeholder. */
  itemCreation?: PlaceholderItemCreation;
}

export const PlaceholderSelection = ({
  projectId,
  docId,
  item,
  kind,
  expandedItemIds,
  onUpdateDataRefetchQueries,
  refetchWizardQuestions,
  handleClose,
  onApplied,
  embedded,
  itemCreation,
}: Props) => {
  const placeholderKind = kind ?? getItemPlaceholderKind(item);

  if (placeholderKind === null) return null;

  switch (placeholderKind) {
    case "template":
      return (
        <TemplatePlaceholderSelectionPanel
          projectId={projectId}
          docId={docId}
          item={item}
          refetchWizardQuestions={refetchWizardQuestions}
          handleClose={handleClose}
          onApplied={onApplied}
          itemCreation={itemCreation}
        />
      );
    case "product":
      return (
        <ProductPlaceholderSelectionPanel
          projectId={projectId}
          docId={docId}
          item={item}
          expandedItemIds={expandedItemIds}
          onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
          refetchWizardQuestions={refetchWizardQuestions}
          handleClose={handleClose}
          onApplied={onApplied}
          embedded={embedded}
        />
      );
    default:
      assertNever(placeholderKind);
  }
};
